import React, { useState, useRef, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import styles from './CustomVideoPlayer.module.css'; 
import { FaPlay, FaPause, FaVolumeMute, FaVolumeUp, FaExpand, FaCompress } from 'react-icons/fa'; 

// --- 1. RECEIVE NEW PROPS ---
// onProgressUpdate(pct: number) — called throttled while playing and on pause/end
const CustomVideoPlayer = ({ src, onAiClick, isAiOpen, onToggleFullScreen, isFullScreen, onProgressUpdate, onError }) => {
  // State
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  // [isFullScreen] state is now managed by the parent
  const [showControls, setShowControls] = useState(true);

  const [seekFlash, setSeekFlash] = useState(null); // 'left' | 'right' | null

  // Refs
  const videoRef = useRef(null);
  const playerContainerRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
  const progressReportTimerRef = useRef(null);
  const lastReportedPctRef = useRef(0);
  const clickTimerRef = useRef(null);
  const seekFlashTimerRef = useRef(null);
  const progressCallbackRef = useRef(onProgressUpdate);
  progressCallbackRef.current = onProgressUpdate;

  useEffect(() => {
    const video = videoRef.current;
    return () => {
      clearTimeout(controlsTimeoutRef.current);
      clearTimeout(progressReportTimerRef.current);
      clearTimeout(clickTimerRef.current);
      clearTimeout(seekFlashTimerRef.current);
      // Save the final partial interval when switching lectures or leaving.
      if (Number.isFinite(video?.duration) && video.duration > 0 && progressCallbackRef.current) {
        const pct = Math.round((video.currentTime / video.duration) * 100);
        if (pct !== lastReportedPctRef.current) progressCallbackRef.current(pct);
      }
    };
  }, []);

  // --- Core Functions ---
  const togglePlay = async () => {
    if (videoRef.current.paused) {
      try {
        await videoRef.current.play();
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('[CustomVideoPlayer] Playback failed:', error);
        toast.error('Could not start playback. Please try again.');
        if (error.name !== 'NotAllowedError') onError?.(error);
      }
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  const seek = (seconds, side) => {
    const vid = videoRef.current;
    if (!vid) return;
    vid.currentTime = Math.min(Math.max(vid.currentTime + seconds, 0), vid.duration || 0);
    setCurrentTime(vid.currentTime);
    // Show flash
    clearTimeout(seekFlashTimerRef.current);
    setSeekFlash(side);
    seekFlashTimerRef.current = setTimeout(() => setSeekFlash(null), 600);
  };

  const handleVideoClick = (e) => {
    // Determine which half of the player was clicked
    const rect = playerContainerRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const isRightHalf = clickX > rect.width / 2;

    if (clickTimerRef.current) {
      // Double click detected
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
      if (isRightHalf) seek(10, 'right');
      else seek(-10, 'left');
    } else {
      // First click — wait to see if double click follows
      clickTimerRef.current = setTimeout(() => {
        clickTimerRef.current = null;
        togglePlay();
      }, 250);
    }
  };

  // Fullscreen function is now in parent

  // Volume
  const handleVolumeChange = (e) => {
    const newVolume = Number(e.target.value);
    videoRef.current.volume = newVolume;
    videoRef.current.muted = newVolume === 0;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    const newMuteState = !videoRef.current.muted;
    videoRef.current.muted = newMuteState;
    setIsMuted(newMuteState);
    if (!newMuteState) {
        const restoredVolume = videoRef.current.volume || 1;
        videoRef.current.volume = restoredVolume;
        setVolume(restoredVolume);
    }
  };

  // --- Progress reporting helper ---
  const reportProgress = (vid) => {
    if (!progressCallbackRef.current || !vid || !Number.isFinite(vid.duration) || vid.duration <= 0) return;
    const pct = Math.round((vid.currentTime / vid.duration) * 100);
    if (pct !== lastReportedPctRef.current) {
      lastReportedPctRef.current = pct;
      progressCallbackRef.current(pct);
    }
  };

  // --- Event Handlers for <video> ---
  const handleTimeUpdate = () => {
    const vid = videoRef.current;
    setCurrentTime(vid.currentTime);
    // Throttle progress reports to every ~5 seconds of real time
    if (!progressReportTimerRef.current) {
      progressReportTimerRef.current = setTimeout(() => {
        reportProgress(vid);
        progressReportTimerRef.current = null;
      }, 5000);
    }
  };

  const handleLoadedMetadata = () => {
    setDuration(videoRef.current.duration);
  };

  const handleSeek = (e) => {
    const newTime = e.target.value;
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  // --- Show/Hide Controls ---
  const hideControls = useCallback(() => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      if(videoRef.current && !videoRef.current.paused) { // Don't hide if paused
        setShowControls(false);
      }
    }, 3000); // Hide after 3 seconds
  }, []);

  const showAndAutoHideControls = useCallback(() => {
    setShowControls(true);
    hideControls();
  }, [hideControls]);
  
  // Effect to show controls when paused/played
  useEffect(() => {
    showAndAutoHideControls();
  }, [isPlaying, showAndAutoHideControls]);

  // Fullscreen listener is now in parent

  // Format time (e.g., 01:05)
  const formatTime = (time) => {
    if (isNaN(time)) return "00:00";
    const minutes = Math.floor(time / 60).toString().padStart(2, '0');
    const seconds = Math.floor(time % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  };


  return (
    <div 
      className={styles.playerContainer} 
      ref={playerContainerRef} 
      onMouseMove={showAndAutoHideControls}
      onMouseLeave={hideControls}
    >
      {seekFlash && (
        <div className={`${styles.seekFlash} ${styles[seekFlash]}`}>
          <div className={styles.seekIcon}>{seekFlash === 'left' ? '⟨⟨' : '⟩⟩'}</div>
          <div className={styles.seekLabel}>{seekFlash === 'left' ? '−10s' : '+10s'}</div>
        </div>
      )}

      <video
        ref={videoRef}
        aria-label="Lecture video"
        src={src}
        className={styles.videoElement}
        onClick={handleVideoClick}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onError={onError}
        onPlay={() => setIsPlaying(true)}
        onEnded={() => { setIsPlaying(false); reportProgress(videoRef.current); }}
        onPause={() => { setIsPlaying(false); reportProgress(videoRef.current); }}
        controls={false}
        playsInline
      />
      
      <div className={`${styles.controlsContainer} ${showControls ? styles.visible : ""}`}>
        {/* Progress Bar */}
        <input
          type="range"
          aria-label="Lecture position"
          min="0"
          max={duration}
          value={currentTime}
          className={styles.progressBar}
          onChange={handleSeek}
        />

        {/* Bottom Controls */}
        <div className={styles.bottomControls}>
          <div className={styles.leftControls}>
            <button onClick={togglePlay} className={styles.controlButton} aria-label={isPlaying ? 'Pause lecture' : 'Play lecture'}>
              {isPlaying ? <FaPause /> : <FaPlay />}
            </button>
            <button onClick={toggleMute} className={styles.controlButton} aria-label={isMuted ? 'Unmute lecture' : 'Mute lecture'}>
              {isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
            </button>
            <input
              type="range"
              aria-label="Lecture volume"
              min="0"
              max="1"
              step="0.1"
              value={isMuted ? 0 : volume}
              className={styles.volumeSlider}
              onChange={handleVolumeChange}
            />
            <span className={styles.timeDisplay}>
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>

          <div className={styles.rightControls}>
            {/* This button just toggles the AI state */}
            <button className={styles.aiButton} onClick={onAiClick}>
              {isAiOpen ? "Hide AI" : "💬 Ask AI"}
            </button>
            
            {/* --- 2. UPDATED FULLSCREEN BUTTON --- */}
            <button onClick={onToggleFullScreen} className={styles.controlButton}>
              {isFullScreen ? <FaCompress /> : <FaExpand />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomVideoPlayer;